import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";
import { RootErrorBoundary } from "./components/root-error-boundary";
import { installChunkReloadHandler } from "./lib/chunkReload";
import { getStoredConsent, loadGoogleAnalytics, loadSentry } from "./lib/consent";

// Catch stale-chunk failures that escape React (uncaught import() rejections /
// script error events) and force a one-time hard reload onto the fresh bundle.
installChunkReloadHandler();

createRoot(document.getElementById("root")!).render(
  <RootErrorBoundary>
    <App />
  </RootErrorBoundary>,
);

// NOTE: the reload-loop guard flag is intentionally NOT cleared here. render()
// is asynchronous and does not mean the lazy route chunks loaded successfully,
// so clearing now would let a still-failing chunk reload over and over. The
// guard is cleared from inside <App> only after the app has stayed alive long
// enough to prove the current bundle is healthy (see App.tsx).

// Non-essential trackers (Google Analytics + the heavy ~258 kB Sentry Session
// Replay integration) are gated behind cookie consent. They only start if the
// visitor previously accepted analytics; first-time and declined visitors get
// nothing until they accept via the cookie disclosure banner (which calls
// initConsentedTrackers directly, no reload). Both are still loaded lazily after
// first paint to stay off the critical path. The local RootErrorBoundary covers
// render errors regardless.
function startTrackers() {
  loadGoogleAnalytics();
  loadSentry();

  // 🔴 Emit the landing page_view HERE, for returning visitors only.
  //
  // Without this, a returning visitor's entry page is never counted at all.
  // The chain is deterministic, not a race:
  //
  //   1. This runs behind requestIdleCallback (up to 4s) to stay off the
  //      critical path, so `window.gtag` does not exist for the first seconds.
  //   2. App.tsx's <AnalyticsTracker> runs its effect milliseconds after first
  //      commit, finds `typeof gtag !== "function"`, and returns early.
  //   3. Its dependency array is [location], so it never re-runs without a
  //      route change — and by then the entry page is long gone.
  //
  // Measured on prod 2026-09-07 with consent already stored: 0 page_views on
  // load, then exactly 1 per client-side route change. Landing-page reports,
  // entrances, bounce rate and session-start attribution were all missing their
  // first and most important row — the page paid traffic actually arrives on.
  //
  // ⚠️ This belongs here and NOT in loadGoogleAnalytics(). The cookie banner's
  // accept() calls initConsentedTrackers() and then fires its own page_view for
  // the acceptance page; moving this into the shared loader would double-count
  // every first-time acceptance. The two paths are deliberately disjoint:
  // banner accept() covers first-time visitors, this covers returning ones, and
  // <AnalyticsTracker> covers every navigation after either.
  //
  // Known residual: if the visitor navigates within the idle delay, this
  // reports where they are NOW rather than where they entered. That is strictly
  // better than today's nothing, and reporting a page they already left would
  // be its own lie. Fixing that properly means giving AnalyticsTracker a
  // "trackers ready" signal, which is a bigger change than this defect warrants.
  const gtag = (window as Window & { gtag?: (...args: unknown[]) => void }).gtag;
  if (typeof gtag === "function") {
    gtag("event", "page_view", {
      page_path: window.location.pathname + window.location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }
}

if (getStoredConsent() === "accepted") {
  const ric = (window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void;
  }).requestIdleCallback;

  if (typeof ric === "function") {
    ric(startTrackers, { timeout: 4000 });
  } else {
    setTimeout(startTrackers, 2000);
  }
}
